import { AreIdentical } from 'commonUtil';
import { z } from 'zod';

// NOTE! We should generally not use strict here in the object since it should be possible to add
//       properties and run a new schema through an older version without stuff breaking
// NOTE! We are doing separate zod and typescript types here to get better typescript hints
//       Check the validation last in the file to make sure types and zod are equal

//
// Types
//

export type ComponentConfigContext = { name: string; identifier: string };

export type ComponentConfigParameter = {
  type: 'string' | 'boolean' | 'integer' | 'secret';
  identifier: string;
  name: string;
  description?: string | undefined;
  defaultValue?: string | number | boolean | undefined;
  stages?: string[] | undefined;
  readOnly?: boolean;
};

export type ComponentConfigParameterGroup = {
  identifier: string;
  name: string;
  parameters: ComponentConfigParameter[];
  description?: string | undefined;
  toggleable?: boolean | undefined;
  defaultValue?: boolean | undefined;
};

export type ComponentConfigEnvironmentVariable = {
  identifier: string;
  defaultValue: string | number | boolean;
};

export type ComponentConfigHost = { name: string; identifier: string; urlFormat: string };

export type ComponentConfigConnectionString = {
  name: string;
  value: string;
  description?: string | undefined;
};

export type ComponentConfigJob =
  | { name: string; type: 'exec'; command: string[]; labelSelector: string }
  | { name: string; type: 'log'; labelSelector: string };

export type ComponentConfigImage = {
  identifier: string;
  image?: string;
  prebuildImage?: string;
};

export type ComponentConfigStorage = { identifier: string; sizeMiB: number; stages?: string[]; uid?: number; gid?: number };

export type ComponentConfig = {
  diploiVersion: 'v1.0';
  type: 'component' | 'addon';
  name: string;
  description?: string;
  requirements?: { ramGiB?: number | undefined };
  contexts: ComponentConfigContext[];
  parameterGroups: ComponentConfigParameterGroup[];
  environmentVariables: ComponentConfigEnvironmentVariable[];
  hosts: ComponentConfigHost[];
  connectionStrings?: ComponentConfigConnectionString[];
  logs: ComponentConfigJob[];
  actions: ComponentConfigJob[];
  images?: ComponentConfigImage[];
  storage: ComponentConfigStorage[];
};

//
// Zod schemas
//

export const componentConfigContextSchema: z.ZodType<ComponentConfigContext> = z.object({
  name: z.string().min(1),
  identifier: z.string().min(1),
});

export const componentConfigParameterSchema: z.ZodType<ComponentConfigParameter> = z.object({
  identifier: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  type: z.enum(['string', 'secret', 'integer', 'boolean']), // TODO: Integer & number are both used here, we should spec this better...
  readOnly: z.boolean().optional(),
  defaultValue: z.string().or(z.number()).or(z.boolean()).optional(),
  stages: z.array(z.string()).optional(),
});

export const componentConfigParameterGroupSchema: z.ZodType<ComponentConfigParameterGroup> = z.object({
  identifier: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  toggleable: z.boolean().optional(),
  defaultValue: z.boolean().optional(),
  parameters: z.array(componentConfigParameterSchema),
});

export const componentConfigEnvironmentVariableSchema: z.ZodType<ComponentConfigEnvironmentVariable> = z.object({
  identifier: z.string().min(1),
  defaultValue: z.string().or(z.number()).or(z.boolean()),
});

export const componentConfigHostSchema: z.ZodType<ComponentConfigHost> = z.object({
  identifier: z.string().min(1),
  name: z.string().min(1),
  urlFormat: z.string().min(1),
});

export const componentConfigConnectionStringSchema: z.ZodType<ComponentConfigConnectionString> = z.object({
  name: z.string().min(1),
  value: z.string().min(1),
  description: z.string().optional(),
});

export const componentConfigJobSchema: z.ZodType<ComponentConfigJob> = z.union([
  z.object({
    name: z.string().min(1),
    type: z.literal('exec'),
    command: z.array(z.string()),
    labelSelector: z.string().min(1),
  }),
  z.object({
    name: z.string().min(1),
    type: z.literal('log'),
    labelSelector: z.string().min(1),
  }),
]);

export const componentConfigImageSchema: z.ZodType<ComponentConfigImage> = z.object({
  identifier: z.string().min(1),
  image: z.string().optional(),
  prebuildImage: z.string().optional(),
});

export const componentConfigStorageSchema: z.ZodType<ComponentConfigStorage> = z.object({
  identifier: z.string().min(1),
  stages: z.array(z.string().min(1)).optional(),
  sizeMiB: z.number(),
  uid: z.number().int().optional(),
  gid: z.number().int().optional(),
});

export const componentConfigSchema /*: z.ZodType<ComponentConfigDocument>*/ = z.object({
  diploiVersion: z.literal('v1.0'),
  type: z.enum(['component', 'addon']),
  name: z.string().min(1),
  description: z.string().optional(),
  contexts: z.array(componentConfigContextSchema).optional().default([]),
  requirements: z.object({ ramGiB: z.number().int().optional() }).optional(),
  parameterGroups: z.array(componentConfigParameterGroupSchema).optional().default([]),
  environmentVariables: z.array(componentConfigEnvironmentVariableSchema).optional().default([]),
  hosts: z.array(componentConfigHostSchema).optional().default([]),
  connectionStrings: z.array(componentConfigConnectionStringSchema).optional(),
  logs: z.array(componentConfigJobSchema).optional().default([]),
  actions: z.array(componentConfigJobSchema).optional().default([]),
  images: z.array(componentConfigImageSchema).optional(),
  storage: z.array(componentConfigStorageSchema).optional().default([]),
});
export const _testComponentConfig: AreIdentical<ComponentConfig, z.infer<typeof componentConfigSchema>> = true;
